import { EVENT_SITES } from '@crossbeam/itly';

import type { PartnerCandidateSourceType } from '@/components/partner-tracker/types';
import type { MatchCountType } from '@/components/partner-tracker/usePartnerTrackerCrmMatchCount';

import useIteratively from '@/composables/useIteratively';
import type { FeedIntegrationType } from '@/types/feeds';

const EVENT_NAME = 'partner_wishlist_action';

type BaseProperties = {
  event_site: string;
  type: string;
};

type CrmSyncModalPropertiesType = {
  mode: 'define' | 'sync';
  feeds: { crmType: FeedIntegrationType; matchCount: MatchCountType }[];
  isDefinitionEdited: boolean;
  roomLeft: number | null;
};

export type EntryPointType = 'empty_state' | 'add_partners_menu';

const SOURCE_COUNT_KEYS = {
  crm: 'crm_count',
  csv_import: 'csv_count',
  manual: 'manual_count',
} as const satisfies Record<PartnerCandidateSourceType, string>;

type SourceCountsType = Record<
  (typeof SOURCE_COUNT_KEYS)[PartnerCandidateSourceType],
  number
>;

function countBySource(sources: (string | null)[]) {
  const counts: SourceCountsType = {
    crm_count: 0,
    csv_count: 0,
    manual_count: 0,
  };
  for (const source of sources) {
    if (source && Object.hasOwn(SOURCE_COUNT_KEYS, source))
      counts[SOURCE_COUNT_KEYS[source as PartnerCandidateSourceType]] += 1;
  }
  return counts;
}

function toCrmSyncModalProperties({
  mode,
  feeds,
  isDefinitionEdited,
  roomLeft,
}: CrmSyncModalPropertiesType) {
  return {
    mode,
    feeds: feeds.map(({ crmType, matchCount }) => ({
      crm_type: crmType,
      match_status: matchCount.status,
      ...(matchCount.status === 'counted' && {
        matched_count: matchCount.count,
      }),
    })),
    definition_edited: isDefinitionEdited,
    room_left: roomLeft,
  };
}

function usePartnerTrackerTracking() {
  const { iteratively } = useIteratively();

  function track(properties: BaseProperties & Record<string, unknown>) {
    const presentProperties = Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value != null),
    );
    // @ts-expect-error itly JSDoc types event.properties as string, but it accepts an object at runtime
    iteratively.track({ name: EVENT_NAME, properties: presentProperties });
  }

  function trackUploadModalOpened(entryPoint: EntryPointType) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'upload_modal_opened',
      entry_point: entryPoint,
    });
  }

  function trackCsvUploaded(totalRows: number, hasErrors: boolean) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'csv_uploaded',
      total_rows: totalRows,
      has_errors: hasErrors,
    });
  }

  function trackUploadConfirmed(
    total: number,
    onCrossbeamCount: number,
    domainsOverLimit: number,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'upload_confirmed',
      total_partners: total,
      on_crossbeam_count: onCrossbeamCount,
      domains_over_limit: domainsOverLimit,
    });
  }

  function trackTemplateDownloaded() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_MODAL,
      type: 'template_downloaded',
    });
  }

  function trackPostUploadInviteSent(
    option: string,
    count: number,
    onCrossbeamCount: number,
    hasMessage: boolean,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_UPLOAD_SUMMARY_MODAL,
      type: 'post_upload_invite_sent',
      invite_option: option,
      invite_count: count,
      on_crossbeam_count: onCrossbeamCount,
      has_message: hasMessage,
    });
  }

  function trackSingleInviteSent(
    isOnCrossbeam: boolean,
    contactCount: number,
    source: string | null,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_INVITE_MODAL,
      type: 'single_invite_sent',
      is_on_crossbeam: isOnCrossbeam,
      contact_count: contactCount,
      source,
    });
  }

  function trackSingleNudgeSent(
    isOnCrossbeam: boolean,
    contactCount: number,
    source: string | null,
    activationTeamCc: boolean | null,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_NUDGE_MODAL,
      type: 'single_nudge_sent',
      is_on_crossbeam: isOnCrossbeam,
      contact_count: contactCount,
      source,
      activation_team_cc: activationTeamCc,
    });
  }

  function trackBulkInviteSent(
    count: number,
    onCrossbeamCount: number,
    hasMessage: boolean,
    sources: (string | null)[],
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_BULK_INVITE_MODAL,
      type: 'bulk_invite_sent',
      invite_count: count,
      on_crossbeam_count: onCrossbeamCount,
      has_message: hasMessage,
      ...countBySource(sources),
    });
  }

  function trackPartnerRemoved(source: string | null) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_TABLE,
      type: 'partner_removed',
      source,
    });
  }

  function trackBulkRemove(count: number, sources: (string | null)[]) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_BULK_REMOVE_MODAL,
      type: 'bulk_remove',
      remove_count: count,
      ...countBySource(sources),
    });
  }

  function trackCrmSyncClicked(
    isConfigured: boolean,
    isDefinitionBlocked: boolean,
    entryPoint: EntryPointType,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_CRM_SYNC_MODAL,
      type: 'crm_sync_clicked',
      is_configured: isConfigured,
      definition_blocked: isDefinitionBlocked,
      entry_point: entryPoint,
    });
  }

  function trackCrmSyncModalConfirmed(
    properties: CrmSyncModalPropertiesType,
    syncStatus: 'started' | 'already_running' | 'rule_saved' | 'error',
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_CRM_SYNC_MODAL,
      type: 'crm_sync_modal_confirmed',
      ...toCrmSyncModalProperties(properties),
      sync_status: syncStatus,
    });
  }

  function trackCrmSyncModalCancelled(properties: CrmSyncModalPropertiesType) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_CRM_SYNC_MODAL,
      type: 'crm_sync_modal_cancelled',
      ...toCrmSyncModalProperties(properties),
    });
  }

  function trackCrmSetupCallout(
    action: 'shown' | 'action_clicked' | 'dismissed',
    crmType: FeedIntegrationType | null,
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_CRM_SETUP_CALLOUT,
      type: `crm_setup_callout_${action}`,
      crm_type: crmType,
    });
  }

  function trackCrmSyncResultCallout(
    action: 'shown' | 'dismissed',
    properties: { added: number; connected: number; at_limit: boolean },
  ) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_CRM_SYNC_RESULT_CALLOUT,
      type: `crm_sync_result_callout_${action}`,
      ...properties,
    });
  }

  function trackOwnerChanged() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_TABLE,
      type: 'owner_changed',
    });
  }

  function trackContactsUpdated(
    eventSite: string = EVENT_SITES.PARTNER_WISHLIST_TABLE,
  ) {
    track({
      event_site: eventSite,
      type: 'contacts_updated',
    });
  }

  // Fired when a request recipient submits a decline WITH a reason
  // (Partner Tracker only). `reason` is the DeclineReasonType enum value.
  function trackDeclineSubmitted(reason: string, hasNote: boolean) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_DECLINE_MODAL,
      type: 'decline_submitted',
      decline_reason: reason,
      has_message: hasNote,
    });
  }

  // Fired when the sender opens the "View reason" modal on a declined row.
  // `reason` may be null when the decliner chose "prefer not to say" (the BE
  // masks it before it reaches the sender).
  function trackDeclineReasonViewed(reason: string | null, hasNote: boolean) {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_VIEW_REASON_MODAL,
      type: 'decline_reason_viewed',
      ...(reason != null && { decline_reason: reason }),
      has_message: hasNote,
    });
  }

  // Fired when the sender clicks "Invite again" from the view-reason modal.
  function trackReinviteFromDecline() {
    track({
      event_site: EVENT_SITES.PARTNER_WISHLIST_VIEW_REASON_MODAL,
      type: 'reinvite_from_decline',
    });
  }

  return {
    trackUploadModalOpened,
    trackCsvUploaded,
    trackUploadConfirmed,
    trackTemplateDownloaded,
    trackPostUploadInviteSent,
    trackSingleInviteSent,
    trackSingleNudgeSent,
    trackBulkInviteSent,
    trackPartnerRemoved,
    trackBulkRemove,
    trackOwnerChanged,
    trackContactsUpdated,
    trackDeclineSubmitted,
    trackDeclineReasonViewed,
    trackReinviteFromDecline,
    trackCrmSyncClicked,
    trackCrmSyncModalConfirmed,
    trackCrmSyncModalCancelled,
    trackCrmSetupCallout,
    trackCrmSyncResultCallout,
  };
}

export { usePartnerTrackerTracking };
